import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import Loader from '../../../../../components/loader'
import AverageVolumePerConsumer from './AverageVolumePerConsumer'
import { trackEvent } from '../../../../../_common/functions'
import InconclusiveReasonModal from '../../../../../components/modal/InconclusiveReason.modal'

type CombinationSupport = 'sufficient' | 'insufficient'

interface CombinationItem {
    id: string
    name: string
    touchpoints: string[]
    nPocs: number
    liftPerPoc: number | null
    liftPct: number | null
    totalEffect: number | null
    interval90: number | null
    Confidence_Level?: number | null
    observedMonthly: number | null
    modeledBauMonthly: number | null
    support: CombinationSupport
    status: string
    items?: CombinationItem[] | null
    Remark?: string | null
}

type MTECardApiItem = {
    id?: string
    Level?: string
    liftPerPoc?: number | null
    Lift?: number | null
    liftPct?: number | null
    totalEffect?: number | null
    Total_Effect_From_Exp?: number | null
    interval90?: number | null
    Confidence_Level?: number | null
    observedMonthly?: number | null
    avg_post_for_treatment?: number | null
    modeledBauMonthly?: number | null
    avg_post_synthetic_control?: number | null
    support?: CombinationSupport
    nPocs?: number
    Converted_Exp_Unit_Treatment?: number
    Exp_Status?: string
    Remark?: string | null
}

type SelectedKpiOption = {
    value?: string | null
    label?: string | null
}

interface Props {
    experimentDetails: any
    selectedGroup?: string
    selectedKPI: string | SelectedKpiOption | null
    selectedDateRange?: any[]
    selectedOptionConfig?: string | null
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
    methodology?: string | null
    mteCardData?: MTECardApiItem[] | null
}

const BASE_COMBINATIONS: CombinationItem[] = []

const resolveUnit = (experimentDetails: any, selectedKPI: string | null) => {
    const metric = experimentDetails?.experiment_metadatas?.metrices?.find((item: any) => item?.literral_value === selectedKPI || item?.kpi_literal === selectedKPI)

    return metric?.kpi_unit?.trim() || 'hL'
}

const resolveKpiLabel = (experimentDetails: any, selectedKPI: string | null) => {
    const metric = experimentDetails?.experiment_metadatas?.metrices?.find((item: any) => item?.literral_value === selectedKPI || item?.kpi_literal === selectedKPI)

    return metric?.literral_value || selectedKPI || 'Volume'
}

const getSignedPrefix = (value: number) => {
    if (value > 0) return '+'
    if (value < 0) return '−'
    return ''
}

const formatSigned = (value: number | null, digits = 2, forConfidenceLevel = false) => {
    if (value == null || Number.isNaN(Number(value))) {
        if (forConfidenceLevel) {
            return 0
        }
        return 'Not Applicable'
    }

    let normalized = Number(value)
    const prefix = getSignedPrefix(normalized)

    if (forConfidenceLevel) {
        if (normalized > 99.99) {
            normalized = 99.99
        }
        return `${Math.abs(normalized).toLocaleString('en-US', {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        })}%`
    }

    return `${prefix}${Math.abs(normalized).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })}`
}

const formatPlain = (value: number | null, digits = 1) => {
    if (value == null || Number.isNaN(Number(value))) return 'Not Applicable'

    return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })
}

const formatCompactSigned = (value: number | null) => {
    if (value == null || Number.isNaN(Number(value))) return 'Not Applicable'

    const normalized = Number(value)
    const prefix = getSignedPrefix(normalized)
    const absolute = Math.abs(normalized)

    if (absolute >= 1000000) {
        return `${prefix}${formatPlain(absolute / 1000000, 2)}M`
    }

    if (absolute >= 1000) {
        return `${prefix}${formatPlain(absolute / 1000, 2)}K`
    }

    return `${prefix}${formatPlain(absolute, 1)}`
}

const getStatus = (status: string) => {
    if (status === 'Insufficient data') {
        return {
            label: 'Insufficient data',
            className: 'text-[#67707A] bg-[#EDF0F2]',
        }
    }
    if (status === 'Positive_Lift') {
        return {
            label: 'Positive Lift',
            className: 'text-[#07845F] bg-[#DFF4EC]',
        }
    }

    if (status === 'Negative_Lift') {
        return {
            label: 'Negative Lift',
            className: 'text-[#C53B3B] bg-[#FBE8E8]',
        }
    }

    if (status === 'No_impact') {
        return {
            label: 'No Impact',
            className: 'text-[#67707A] bg-[#EDF0F2]',
        }
    }

    if (status.includes('Inconclusive')) {
        return {
            label: status.replace(/_/g, ' '),
            className: 'text-[#A76D00] bg-[#FFF0C8]',
        }
    }

    return {
        label: 'Unknown',
        className: 'text-[#A76D00] bg-[#FFF0C8]',
    }
}

const getSelectedKpiValue = (selectedKPI: Props['selectedKPI']) => {
    if (typeof selectedKPI === 'string') return selectedKPI

    return selectedKPI?.value || selectedKPI?.label || null
}

const getSelectedKpiLabel = (selectedKPI: Props['selectedKPI']) => {
    if (typeof selectedKPI === 'string') return selectedKPI

    return selectedKPI?.label || selectedKPI?.value || null
}

const normalizeTouchpoints = (level: string) =>
    level
        .split('+')
        .map((item) => item.trim())
        .filter(Boolean)

const buildCombinations = (apiData?: MTECardApiItem[] | null): CombinationItem[] => {
    const combinations = BASE_COMBINATIONS.length ? BASE_COMBINATIONS.map((item) => ({ ...item, touchpoints: [...item.touchpoints], interval90: item.interval90 ?? null })) : []

    if (!apiData?.length) {
        return combinations
    }

    const insufficientItems: MTECardApiItem[] = []

    apiData.forEach((data) => {
        const status = data.Exp_Status ?? (data.support === 'insufficient' ? 'Insufficient data' : 'Positive_Lift')

        if (!data.Level) {
            return
        }

        if (status === 'Insufficient data' || status === 'Inconclusive-Sample_Insufficiency') {
            insufficientItems.push(data)
            return
        }

        combinations.push({
            id: data.id ?? data.Level,
            name: data.Level,
            touchpoints: normalizeTouchpoints(data.Level),
            liftPerPoc: data.liftPerPoc ?? data.Lift ?? null,
            liftPct: data.liftPct ?? 2.1,
            totalEffect: data.totalEffect ?? data.Total_Effect_From_Exp ?? null,
            interval90: data.Confidence_Level ? data.Confidence_Level * 100 : null,
            Confidence_Level: data.Confidence_Level ? data.Confidence_Level * 100 : null,
            observedMonthly: data.observedMonthly ?? data.avg_post_for_treatment ?? null,
            modeledBauMonthly: data.modeledBauMonthly ?? data.avg_post_synthetic_control ?? null,
            support: data.support ?? 'sufficient',
            nPocs: data.nPocs ?? data.Converted_Exp_Unit_Treatment ?? 0,
            Remark: data.Remark ?? null,
            status,
        })
    })

    if (insufficientItems.length > 0) {
        const aggregatedNpcs = insufficientItems.reduce((sum, item) => sum + (item.nPocs ?? item.Converted_Exp_Unit_Treatment ?? 0), 0)
        const aggregatedTouchpoints: CombinationItem[] = []
        insufficientItems.forEach((data) => {
            const status = data.Exp_Status ?? (data.support === 'insufficient' ? 'Insufficient data' : 'unknown')
            aggregatedTouchpoints.push({
                id: data.id ?? data.Level ?? 'Insufficient data',
                name: data.Level || 'Insufficient data',
                touchpoints: normalizeTouchpoints(data.Level || 'Insufficient data'),
                liftPerPoc: data.liftPerPoc ?? data.Lift ?? null,
                liftPct: data.liftPct ?? 2.1,
                totalEffect: data.totalEffect ?? data.Total_Effect_From_Exp ?? null,
                interval90: data.Confidence_Level ? data.Confidence_Level * 100 : null,
                Confidence_Level: data.Confidence_Level ? data.Confidence_Level * 100 : null,
                observedMonthly: data.observedMonthly ?? data.avg_post_for_treatment ?? null,
                modeledBauMonthly: data.modeledBauMonthly ?? data.avg_post_synthetic_control ?? null,
                support: data.support ?? 'sufficient',
                nPocs: data.nPocs ?? data.Converted_Exp_Unit_Treatment ?? 0,
                Remark: data.Remark ?? null,
                status,
            })
        })

        combinations.push({
            id: 'insufficient-data-group',
            name: 'Insufficient data',
            touchpoints: aggregatedTouchpoints.flatMap((item) => item.touchpoints),
            items: aggregatedTouchpoints,
            liftPerPoc: null,
            liftPct: null,
            totalEffect: null,
            interval90: null,
            Confidence_Level: null,
            observedMonthly: null,
            modeledBauMonthly: null,
            Remark: null,
            support: 'insufficient',
            nPocs: aggregatedNpcs,
            status: 'Insufficient data',
        })
    }
    return combinations
}

const MTEKpiCards: React.FC<Props> = ({ experimentDetails, selectedGroup = null, selectedKPI, selectedDateRange = [], selectedOptionConfig = null, toDownloadAsReport = false, reportError, methodology = null, mteCardData = null }) => {
    const { t } = useTranslation()
    const { getMTECardData } = useExperimentApi()
    const [loadedMteCardData, setLoadedMteCardData] = useState<MTECardApiItem[] | null>(null)
    const [isLoadingMteCards, setIsLoadingMteCards] = useState<boolean>(!mteCardData?.length)
    const [isTrendPopupOpen, setIsTrendPopupOpen] = useState<boolean>(false)
    const [selectedTrendGroup, setSelectedTrendGroup] = useState<string | null>(null)
    const [isInconclusiveModalOpen, setIsInconclusiveModalOpen] = useState<boolean>(false)
    const [isNoImpactModalOpen, setIsNoImpactModalOpen] = useState<boolean>(false)
    const [inconclusiveReason, setInconclusiveReason] = useState<string | null>(null)

    const selectedKpiValue = getSelectedKpiValue(selectedKPI)
    const selectedKpiLabel = getSelectedKpiLabel(selectedKPI)
    async function getMTECardDetails(literal: string) {
        return new Promise((resolve) => {
            const params = {
                literal,
                measurement_start_date: null,
                measurement_end_date: null,
                is_slice_and_dice: false,
            }
            getMTECardData(
                params,
                (resp: any) => {
                    resolve(resp.data)
                },
                () => {
                    resolve(null)
                },
            )
        })
    }

    useEffect(() => {
        if (mteCardData?.length) {
            setIsLoadingMteCards(false)
            return
        }

        if (!experimentDetails?.experiment_literal) {
            setIsLoadingMteCards(false)
            return
        }

        setIsLoadingMteCards(true)
        void getMTECardDetails(experimentDetails.experiment_literal)
            .then((resp: any) => {
                setLoadedMteCardData(Array.isArray(resp) ? resp : null)
                setIsLoadingMteCards(false)
            })
            .catch((error) => {
                console.error('Error :', error)
            })
    }, [experimentDetails, mteCardData])

    const combinations = useMemo(() => {
        const nextCombinations = buildCombinations(mteCardData ?? loadedMteCardData)

        return nextCombinations.length > 0 ? nextCombinations : buildCombinations(null)
    }, [loadedMteCardData, mteCardData])
    const availableResults = combinations.filter((item) => item.support === 'sufficient').length
    const activatedPocs = combinations.reduce((sum, item) => sum + item.nPocs, 0)
    const unit = resolveUnit(experimentDetails, selectedKpiValue)
    const kpiLabel = resolveKpiLabel(experimentDetails, selectedKpiLabel)

    if (isLoadingMteCards) {
        return (
            <section className="col-span-3 rounded-[0.45rem] bg-base-100 shadow-1" data-testid="mte-kpi-cards-section">
                <div className="flex min-h-[20rem] items-center justify-center px-3 py-3" data-testid="mte-kpi-cards-loader">
                    <Loader />
                </div>
            </section>
        )
    }

    const isNoImpactWithCustomKPI = (item: any) => {
        if (item?.status?.search('No_impact') == 0) {
            return true
        } else {
            return false
        }
    }

    return (
        <>
            <section className="col-span-3 rounded-[0.45rem] bg-base-100 shadow-1" data-testid="mte-kpi-cards-section">
                <div className="px-3 py-3">
                    <div className="hidden">
                        <div className="grid gap-3 xl:grid-cols-[repeat(3,minmax(0,1fr))_1.28fr]">
                            <article className="rounded-xl bg-[#FFE600] px-5 py-4">
                                <span className="block text-[12px] font-semibold text-[#111315]">{t('Exact Combinations')}</span>
                                <span className="mt-1 block text-[11px] font-medium text-[#474B50]">{t('observed in the selected month')}</span>
                                <strong className="mt-2 block text-[32px] font-semibold leading-none text-[#111315]">{combinations.length}</strong>
                            </article>

                            <article className="rounded-xl bg-[#FFE600] px-5 py-4">
                                <span className="block text-[12px] font-semibold text-[#111315]">{t('Activated POCs')}</span>
                                <span className="mt-1 block text-[11px] font-medium text-[#474B50]">{t('across the exact combinations below')}</span>
                                <strong className="mt-2 block text-[32px] font-semibold leading-none text-[#111315]">{activatedPocs.toLocaleString('en-US')}</strong>
                            </article>

                            <article className="rounded-xl bg-[#FFE600] px-5 py-4">
                                <span className="block text-[12px] font-semibold text-[#111315]">{t('Results Available')}</span>
                                <span className="mt-1 block text-[11px] font-medium text-[#474B50]">{t('combinations with a numeric estimate')}</span>
                                <strong className="mt-2 block text-[32px] font-semibold leading-none text-[#111315]">
                                    {availableResults}
                                    <span className="ml-1 text-sm font-semibold">{t('of {{count}}', { count: combinations.length })}</span>
                                </strong>
                            </article>

                            <aside className="rounded-xl bg-white px-5 py-4 shadow-1">
                                <span className="block text-[12px] font-semibold text-[#111315]">{t('Measurement checks')}</span>
                                <ul className="mt-2 grid list-none grid-cols-1 gap-y-2 pl-0 text-[11px] text-[#111315] md:grid-cols-2 md:gap-x-4">
                                    {['Combination coverage', 'Modeled BAU fit', 'Uncertainty estimated', 'Results generated'].map((item) => (
                                        <li key={item} className="flex items-center gap-2 before:block before:h-[7px] before:w-[7px] before:rounded-full before:bg-[#16A34A] before:content-['']">
                                            {t(item)}
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                        </div>
                    </div>

                    <p className="mt-4 flex items-start gap-3 rounded-[9px] border border-[#D9E4F3] bg-[#EDF4FD] px-4 py-3 text-[12px] text-[#24476F]">
                        <span className="flex h-[23px] w-[23px] shrink-0 items-center justify-center rounded-full bg-[#2B5D99] text-xs font-bold text-white">i</span>
                        <span>
                            <strong>{t('Every result has its own modeled BAU.')}</strong> {t('It estimates the monthly sales those same POCs would have had without that exact Test combination; it is not the simple Control average.')}
                        </span>
                    </p>

                    <div className="mt-4 rounded-xl border border-[#ECECEE] bg-white p-5 shadow-1">
                        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-[#111315]">{t('All exact touchpoint combinations')}</h2>
                                <p className="text-xs text-[#62676E]">{t('Each card is a separate result for POCs receiving exactly that combination in the selected Analysis Range.')}</p>
                            </div>
                            <span className="w-fit rounded-full bg-[#F2F3F4] px-3 py-1 text-[11px] font-semibold text-[#50555C]">{t('{{count}} combinations', { count: combinations.length })}</span>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {combinations.map((item) => {
                                const status = getStatus(item.status)
                                const isInsufficient = item.status == 'Insufficient data'
                                const formattedPocs = item.nPocs.toLocaleString('en-US')
                                const showTrendButton = item.status != 'Insufficient data'

                                return (
                                    <article key={item.id} className="flex min-h-[258px] flex-col rounded-[11px] border border-[#E1E2E4] bg-white p-4">
                                        {isInsufficient ? (
                                            <>
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <h3 className="text-[12px] font-semibold leading-5 text-[#111315]">{t(item.name)}</h3>
                                                        {/* <div className="mt-2 flex flex-wrap gap-1.5">
                                                        {item.touchpoints.map((touchpoint) => (
                                                            <span key={touchpoint} className="inline-flex h-[21px] items-center rounded-[6px] bg-[#F0F1F2] px-2 text-[10px] font-semibold uppercase tracking-[0.2px] text-[#4F555D]">
                                                                {t(touchpoint)}
                                                            </span>
                                                        ))}
                                                    </div> */}
                                                    </div>

                                                    <span className={`inline-flex min-h-[25px] items-center rounded-full px-3 py-1 text-[11px] font-semibold ${status.className}`}>{t(status.label)}</span>
                                                </div>

                                                <div className="mt-4 overflow-hidden rounded-lg border border-[#E5E6E8] bg-[#FAFAFA]">
                                                    <table className="w-full border-collapse text-left text-[11px] text-[#111315]">
                                                        <thead>
                                                            <tr className="bg-[#F5F6F7] text-[#4F555D]">
                                                                <th className="px-3 py-2 font-semibold">{t('Level')}</th>
                                                                <th className="px-3 py-2 font-semibold">{t('Activated POCs')}</th>
                                                                {/* <th className="px-3 py-2 font-semibold">{t('Confidence level')}</th> */}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(item.items ?? []).map((subItem, index) => {
                                                                const subitemformattedPocs = subItem.nPocs.toLocaleString('en-US')
                                                                return (
                                                                    <tr key={`${item.id}-${subItem.id ?? subItem.name ?? 'row'}-${index}`} className="border-t border-[#E5E6E8]">
                                                                        <td className="px-3 py-2">
                                                                            {subItem.touchpoints.map((touchpoint) => (
                                                                                <span key={touchpoint} className="inline-flex h-[21px] items-center rounded-[6px] bg-[#F0F1F2] px-2 text-[10px] font-semibold uppercase tracking-[0.2px] text-[#4F555D]">
                                                                                    {t(touchpoint)}
                                                                                </span>
                                                                            ))}
                                                                        </td>
                                                                        <td className="px-3 py-2 text-right">{subitemformattedPocs ?? 'Not Applicable'}</td>
                                                                        {/* <td className="px-3 py-2">{subItem.interval90 ? `${formatSigned(subItem.interval90, 2, true)}` : 'Not Applicable'}</td> */}
                                                                    </tr>
                                                                )
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <h3 className="text-[12px] font-semibold leading-5 text-[#111315]">{t(item.name)}</h3>
                                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                                            {item.touchpoints.map((touchpoint, index) => (
                                                                <span
                                                                    key={`${item.id}-${touchpoint}-${index}`}
                                                                    className="inline-flex h-[21px] items-center rounded-[6px] bg-[#F0F1F2] px-2 text-[10px] font-semibold uppercase tracking-[0.2px] text-[#4F555D]"
                                                                >
                                                                    {t(touchpoint)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className={`inline-flex min-h-[25px] items-center rounded-full px-3 py-1 text-[11px] font-semibold ${status.className}`}>{t(status.label)}</span>
                                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                                            {item.Remark && (item.status.includes('Inconclusive') || isNoImpactWithCustomKPI(item)) && (
                                                                <button
                                                                    type="button"
                                                                    className="mt-1 inline-flex items-center text-left font-normal text-[0.591rem] text-[#2B5D99] underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
                                                                    onClick={() => {
                                                                        if (isNoImpactWithCustomKPI(item)) {
                                                                            trackEvent('experiment_details_no_impact_link_clicked', { experiment_literal: experimentDetails?.experiment_literal })
                                                                            setIsNoImpactModalOpen(true)
                                                                            setInconclusiveReason(item.Remark || null)
                                                                        } else {
                                                                            trackEvent('experiment_details_inconclusive_link_clicked', { experiment_literal: experimentDetails?.experiment_literal })
                                                                            setIsInconclusiveModalOpen(true)
                                                                            setInconclusiveReason(item.Remark || null)
                                                                        }
                                                                    }}
                                                                >
                                                                    {t('Click here to know why :')} {t(status.label)}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mt-4 grid gap-2 xl:grid-cols-3">
                                                    <div className="rounded-lg border border-[#E5E6E8] bg-[#FAFAFA] px-3 py-2">
                                                        <span className="block min-h-[25px] text-[10px] font-semibold leading-4 text-[#555B62]">{t('Uplift')}</span>
                                                        <strong className="mt-1 block text-[12px] font-semibold leading-5 text-[#111315]">
                                                            {formatSigned(item.liftPerPoc)}
                                                            {item.liftPerPoc ? '%' : ''}
                                                        </strong>
                                                        {/* <span className="mt-1 block text-[10px] leading-4 text-[#62676E]">
                                                        {t('per POC / month')}
                                                        <br />
                                                        {formatSigned(item.liftPct)}% {t('vs modeled BAU')}
                                                    </span> */}
                                                    </div>

                                                    <div className="rounded-lg border border-[#E5E6E8] bg-[#FAFAFA] px-3 py-2">
                                                        <span className="block min-h-[25px] text-[10px] font-semibold leading-4 text-[#555B62]">{t('Total Effect')}</span>
                                                        <strong className="mt-1 block text-[12px] font-semibold leading-5 text-[#111315]">
                                                            {formatCompactSigned(item.totalEffect)}
                                                            {item.totalEffect ? ' ' + unit : ''}
                                                        </strong>
                                                        {/* <span className="mt-1 block text-[10px] leading-4 text-[#62676E]">
                                                        {t('modeled total in the selected month across')} {formattedPocs} {t('activated POCs')}
                                                    </span> */}
                                                    </div>

                                                    <div className="rounded-lg border border-[#E5E6E8] bg-[#FAFAFA] px-3 py-2">
                                                        <span className="block min-h-[25px] text-[10px] font-semibold leading-4 text-[#555B62]">{t('Confidence Level')}</span>
                                                        <strong className="mt-1 block text-[12px] font-semibold leading-5 text-[#111315]">{item.interval90 ? `${formatSigned(item.interval90, 2, true)}` : 'Not Applicable'}</strong>
                                                        {/* <span className="mt-1 block text-[10px] leading-4 text-[#62676E]">
                                                        {unit} / {t('POC / month')}
                                                    </span> */}
                                                    </div>
                                                </div>

                                                <div className="mt-3 grid grid-cols-2 gap-2">
                                                    <div className="rounded-[7px] bg-[#F7F7F7] px-3 py-2">
                                                        <strong className="block text-[11px] font-semibold text-[#111315]">
                                                            {formatPlain(item.observedMonthly)}
                                                            {item.observedMonthly ? ' ' + unit : ''}
                                                        </strong>
                                                        <span className="text-[9px] text-[#62676E]">{t('Observed {{kpi}}', { kpi: kpiLabel })}</span>
                                                    </div>
                                                    <div className="rounded-[7px] bg-[#F7F7F7] px-3 py-2">
                                                        <strong className="block text-[11px] font-semibold text-[#111315]">
                                                            {formatPlain(item.modeledBauMonthly)}
                                                            {item.modeledBauMonthly ? ' ' + unit : ''}
                                                        </strong>
                                                        <span className="text-[9px] text-[#62676E]">{t('Modeled BAU')}</span>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <div className="mt-auto flex items-center justify-between gap-2 border-t border-[#EEEEEF] pt-3 text-[10px] text-[#62676E]">
                                            {showTrendButton ? (
                                                <>
                                                    <span>
                                                        {formattedPocs} {t('activated POCs')}
                                                    </span>
                                                    <button
                                                        className="trend-button"
                                                        type="button"
                                                        data-trend="promo"
                                                        aria-label={`View sales trend for ${item.name}`}
                                                        onClick={() => {
                                                            setSelectedTrendGroup(item.name)
                                                            setIsTrendPopupOpen(true)
                                                        }}
                                                    >
                                                        View trend
                                                    </button>
                                                </>
                                            ) : null}
                                        </div>
                                    </article>
                                )
                            })}
                        </div>

                        {isTrendPopupOpen ? (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
                                <div className="flex h-[75vh] w-[75vw] flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-2xl">
                                    <div className="mb-4 flex items-start justify-between gap-4">
                                        <div>
                                            <h2 className="text-lg font-semibold text-[#111315]">{t('View sales trend for {{group}}', { group: selectedTrendGroup ?? t('selected combination') })}</h2>
                                            <p className="text-xs text-[#62676E]">{t('Trend details for the selected MTE combination.')}</p>
                                        </div>
                                        <button type="button" className="rounded-full border border-[#D9D9DE] px-3 py-1 text-xs font-semibold text-[#111315]" onClick={() => setIsTrendPopupOpen(false)}>
                                            {t('Close')}
                                        </button>
                                    </div>

                                    <div className="min-h-40 w-full flex-1 overflow-auto">
                                        <AverageVolumePerConsumer
                                            experimentDetails={experimentDetails}
                                            selectedGroup={selectedGroup || 'Test'}
                                            selectedKPI={selectedTrendGroup}
                                            selectedDateRange={selectedDateRange}
                                            selectedOptionConfig={selectedOptionConfig}
                                            toDownloadAsReport={toDownloadAsReport}
                                            mtegrouptext={selectedTrendGroup}
                                            reportError={reportError}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <p className="mt-4 flex items-start gap-3 rounded-r-lg border-l-4 border-l-[#F1D900] bg-[#FAFAFA] px-4 py-3 text-[11px] text-[#484D53]">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B5D99] text-[11px] font-bold text-white">i</span>
                            <span>
                                <strong>{t('Interpret each combination as a whole.')}</strong>{' '}
                                {t(
                                    'Total Effect aggregates the selected month’s estimated per-POC difference across that combination’s activated POCs; it must not be added across cards. Do not treat differences between cards as incremental touchpoint effects or read “Promotions + Rewards only” as the separate contribution of Promotions plus Rewards. Compare the estimate together with its 90% confidence interval and support—not the point estimate alone. These are modeled comparisons, not proof that a combination caused the difference.',
                                )}
                            </span>
                        </p>
                    </div>
                </div>
            </section>
            {(isInconclusiveModalOpen || isNoImpactModalOpen) && (
                <InconclusiveReasonModal
                    literal={experimentDetails.experiment_literal}
                    group_name={null}
                    kpi={null}
                    methodology={null}
                    Reason={inconclusiveReason}
                    isModalOpen={isInconclusiveModalOpen || isNoImpactModalOpen}
                    onClose={() => {
                        setIsInconclusiveModalOpen(false)
                        setIsNoImpactModalOpen(false)
                    }}
                    type={isNoImpactModalOpen ? 'no_impact' : 'inconclusive'}
                />
            )}
        </>
    )
}

export default MTEKpiCards
